import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Alert,
  CircularProgress,
  Snackbar,
  Avatar,
  Grid,
  Input
} from '@mui/material';
import { 
  Add as AddIcon, 
  Edit as EditIcon, 
  Delete as DeleteIcon, 
  PhotoCamera as PhotoCameraIcon,
  Image as ImageIcon
} from '@mui/icons-material';
import { inventoryAPI } from '../services/api';

const InventoryManagement = () => {
  const [inventoryItems, setInventoryItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogMode, setDialogMode] = useState('add'); // 'add' or 'edit'
  const [currentItem, setCurrentItem] = useState({ name: '', totalQuantity: '', photoUrl: '' });
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState('');
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);

  useEffect(() => {
    fetchInventoryItems();
  }, []);

  const fetchInventoryItems = async () => {
    setLoading(true);
    try {
      const response = await inventoryAPI.getAllItems();
      setInventoryItems(response.data);
      setError('');
    } catch (err) {
      setError('Failed to load inventory items. Please try again later.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenAddDialog = () => {
    setDialogMode('add');
    setCurrentItem({ name: '', totalQuantity: '' });
    setPhotoFile(null);
    setPhotoPreview('');
    setOpenDialog(true);
  };

  const handleOpenEditDialog = (item) => {
    setDialogMode('edit');
    setCurrentItem({ ...item });
    setPhotoFile(null);
    setPhotoPreview(item.photoUrl || '');
    setOpenDialog(true);
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setPhotoFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setPhotoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setCurrentItem(prev => ({
      ...prev,
      [name]: name === 'totalQuantity' ? (value === '' ? '' : parseInt(value, 10)) : value
    }));
  };

  const handleSubmit = async () => {
    // Validate input
    if (!currentItem.name || !currentItem.totalQuantity) {
      setError('Name and quantity are required.');
      return;
    }

    if (currentItem.totalQuantity < 0) {
      setError('Quantity must be a positive number.');
      return;
    }

    try {
      if (dialogMode === 'add') {
        if (photoFile) {
          // Create item with photo
          await inventoryAPI.createItemWithPhoto(
            currentItem.name,
            currentItem.totalQuantity,
            photoFile
          );
        } else {
          // Create item without photo
          await inventoryAPI.createItem(currentItem);
        }
        setSnackbarMessage('Inventory item added successfully!');
      } else {
        if (photoFile) {
          // First update the item
          await inventoryAPI.updateItem(currentItem.id, currentItem);

          // Then upload the photo
          await inventoryAPI.uploadPhoto(currentItem.id, photoFile);
        } else {
          // Just update the item
          await inventoryAPI.updateItem(currentItem.id, currentItem);
        }
        setSnackbarMessage('Inventory item updated successfully!');
      }

      fetchInventoryItems();
      setOpenDialog(false);
      setSnackbarOpen(true);
    } catch (err) {
      setError(err.response?.data?.message || 'An error occurred. Please try again.');
    }
  };

  const handleOpenDeleteDialog = (item) => {
    setItemToDelete(item);
    setDeleteDialogOpen(true);
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setItemToDelete(null);
  };

  const handleDeleteConfirm = async () => {
    if (!itemToDelete) return;

    try {
      await inventoryAPI.deleteItem(itemToDelete.id);
      fetchInventoryItems();
      setSnackbarMessage('Inventory item deleted successfully!');
      setSnackbarOpen(true);
    } catch (err) {
      setError('Failed to delete item. Please try again.');
    } finally {
      setDeleteDialogOpen(false);
      setItemToDelete(null);
    }
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container>
      <Box sx={{ mt: 4, mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Управління інвентарем
        </Typography>

        {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 3 }}>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={handleOpenAddDialog}
          >
            Додати інвентар
          </Button>
        </Box>

        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: '50px' }}>ID</TableCell>
                <TableCell sx={{ width: '80px' }}>Фото</TableCell>
                <TableCell>Назва</TableCell>
                <TableCell sx={{ width: '100px', textAlign: 'center' }}>Кількість</TableCell>
                <TableCell sx={{ width: '120px', textAlign: 'center' }}>Дії</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {inventoryItems.length > 0 ? (
                inventoryItems.map((item) => (
                  <TableRow key={item.id}>
                    <TableCell sx={{ width: '50px' }}>{item.id}</TableCell>
                    <TableCell sx={{ width: '80px' }}>
                      {item.photoUrl ? (
                        <Avatar
                          src={item.photoUrl}
                          alt={item.name}
                          variant="rounded"
                          sx={{ width: 60, height: 60 }}
                          imgProps={{ style: { objectFit: 'contain' } }}
                        />
                      ) : (
                        <Avatar
                          variant="rounded"
                          sx={{ width: 60, height: 60, bgcolor: 'grey.300' }}
                        >
                          <ImageIcon />
                        </Avatar>
                      )}
                    </TableCell>
                    <TableCell>{item.name}</TableCell>
                    <TableCell sx={{ width: '100px', textAlign: 'center' }}>{item.totalQuantity}</TableCell>
                    <TableCell sx={{ width: '120px', textAlign: 'center' }}>
                      <IconButton
                        color="primary"
                        onClick={() => handleOpenEditDialog(item)}
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        color="primary"
                        onClick={() => handleOpenDeleteDialog(item)}
                      >
                        <DeleteIcon color="primary" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ padding: '20px 0' }}>
                    Немає інвентаря
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      {/* Add/Edit Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {dialogMode === 'add' ? 'Додати інвентар' : 'Редагувати інвентар'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                autoFocus
                margin="dense"
                name="name"
                label="Назва"
                type="text"
                fullWidth
                variant="outlined"
                value={currentItem.name}
                onChange={handleInputChange}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                margin="dense"
                name="totalQuantity"
                label="Кількість"
                type="number"
                fullWidth
                variant="outlined"
                value={currentItem.totalQuantity}
                onChange={handleInputChange}
                inputProps={{ min: 0 }}
              />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle1" gutterBottom>
                Фото (необов'язково)
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Button
                  variant="contained"
                  component="label"
                  startIcon={<PhotoCameraIcon />}
                >
                  Вибрати фото
                  <input
                    type="file"
                    hidden
                    accept="image/*"
                    onChange={handlePhotoChange}
                  />
                </Button>
                {photoFile && (
                  <Typography variant="body2" sx={{ ml: 2 }}>
                    {photoFile.name}
                  </Typography>
                )}
              </Box>
              {photoPreview && (
                <Box sx={{ mt: 2, textAlign: 'center' }}>
                  <img
                    src={photoPreview}
                    alt="Preview"
                    style={{ maxWidth: '100%', maxHeight: '200px' }}
                  />
                </Box>
              )}
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Скасувати</Button>
          <Button onClick={handleSubmit} variant="contained">
            {dialogMode === 'add' ? 'Додати' : 'Зберегти'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={handleCloseDeleteDialog}>
        <DialogTitle>Видалити інвентар</DialogTitle>
        <DialogContent>
          <Typography>
            Ви впевнені, що хочете видалити "{itemToDelete?.name}"?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog}>Скасувати</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            Видалити
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success Snackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        message={snackbarMessage}
      />
    </Container>
  );
};

export default InventoryManagement;
